import { ADMIN_API_BASE_URL } from '../config'
import { handleApiResponse } from '../utils/commonfunction'
import {
  makeApiRequest,
  addAppTypeToParams,
  addAppTypeToBody,
} from '../utils/apiHelper'

export interface TradingHoursSettings {
  enabled: boolean
  fixed_window_start_time: string | null
  fixed_window_end_time: string | null
  cancel_unexecuted_fixed_limits: boolean
  restrict_to_market_hours: boolean
}

export interface Challenge {
  id: string
  name: string
  description?: string
  type: string
  entry_fee: number
  prize_pool: number
  max_participants?: number
  min_participants?: number
  current_participants: number
  start_date: string
  end_date: string
  status: string
  gameplay_type?: 'DYNAMIC' | 'FIXED'
  allow_overflow_entries?: boolean
  duration_days?: number
  // New fields for challenge configuration
  stock_universe?: string
  stock_universe_ids?: string[]
  trading_hours_settings?: TradingHoursSettings
  virtual_capital?: number
  brokerage_fee?: number
  challenge_type?: 'Flexible' | 'Guaranteed'
  guaranteed_prize_pool?: number
  allowed_order_types?: {
    market_order: boolean
    limit_orders: boolean
    stop_loss_orders: boolean
  }
  trading_restrictions?: {
    allow_short_selling: boolean
    intraday_only: boolean
    max_open_positions?: number
    max_capital_per_stock?: number
    restricted_instruments?: string[]
    trading_hours?: {
      start: string
      end: string
    }
    max_positions?: number
    leverage_allowed?: boolean
    max_leverage?: number
  }
  prize_distribution?: Array<{
    rank: number
    percentage: number
  }>
  rules?: string
  terms_conditions?: string
  created_by: string
  created_at: string
  updated_at: string
  is_active: boolean
  // Additional properties that might come from backend
  max_positions?: number
  leverage_allowed?: boolean
  max_leverage?: number
  // New fields for enhanced challenge configuration
  category?: 'Equity' | 'F&O' | 'Commodities'
  duration_tag?: 'Daily' | 'Weekly' | 'Monthly' | 'Mega' | 'Practice'
  platform_stake?: number
  info_box_text?: string
  first_prize_icon?: string
  win_percentage_icon?: string
  max_entries_icon?: string
  // Backend fields for admin panel
  experience_level?: string
  risk_appetite?: string
  is_guaranteed?: boolean
}

export interface ChallengeCreate {
  name: string
  description?: string
  type: string
  entry_fee: number
  prize_pool: number
  max_participants?: number
  min_participants?: number
  start_date: string
  end_date: string
  gameplay_type?: 'DYNAMIC' | 'FIXED'
  allow_overflow_entries?: boolean
  duration_days?: number
  // New fields for challenge configuration
  stock_universe?: string
  stock_universe_ids?: string[]
  trading_hours_settings?: TradingHoursSettings
  virtual_capital?: number
  brokerage_fee?: number
  challenge_type?: 'Flexible' | 'Guaranteed'
  guaranteed_prize_pool?: number
  allowed_order_types?: {
    market_order: boolean
    limit_orders: boolean
    stop_loss_orders: boolean
  }
  experience_level: string
  risk_appetite: string
  trading_restrictions?: {
    allow_short_selling: boolean
    intraday_only: boolean
    max_open_positions?: number
    max_capital_per_stock?: number
    restricted_instruments?: string[]
    max_positions?: number
    leverage_allowed?: boolean
    max_leverage?: number
  }
  prize_distribution?: Array<{
    rank: number
    percentage: number
  }>
  rules?: string
  terms_conditions?: string
  // New fields for enhanced challenge configuration
  category?: 'Equity' | 'F&O' | 'Commodities'
  duration_tag?: 'Daily' | 'Weekly' | 'Monthly' | 'Mega' | 'Practice'
  platform_stake?: number
  info_box_text?: string
  first_prize_icon?: string
  win_percentage_icon?: string
  max_entries_icon?: string
}

export interface ChallengeUpdate {
  name?: string
  description?: string
  type?: string
  entry_fee?: number
  prize_pool?: number
  max_participants?: number
  min_participants?: number
  start_date?: string
  end_date?: string
  duration_days?: number
  gameplay_type?: 'DYNAMIC' | 'FIXED'
  allow_overflow_entries?: boolean
  // New fields for challenge configuration
  stock_universe?: string
  stock_universe_ids?: string[]
  trading_hours_settings?: TradingHoursSettings
  virtual_capital?: number
  brokerage_fee?: number
  challenge_type?: 'Flexible' | 'Guaranteed'
  guaranteed_prize_pool?: number
  allowed_order_types?: {
    market_order: boolean
    limit_orders: boolean
    stop_loss_orders: boolean
  }
  trading_restrictions?: {
    allow_short_selling: boolean
    intraday_only: boolean
    max_open_positions?: number
    max_capital_per_stock?: number
    restricted_instruments?: string[]
    max_positions?: number
    leverage_allowed?: boolean
    max_leverage?: number
  }
  prize_distribution?: Array<{
    rank: number
    percentage: number
  }>
  rules?: string
  terms_conditions?: string
  status?: string
  // New fields for enhanced challenge configuration
  category?: 'Equity' | 'F&O' | 'Commodities'
  duration_tag?: 'Daily' | 'Weekly' | 'Monthly' | 'Mega' | 'Practice'
  platform_stake?: number
  info_box_text?: string
  first_prize_icon?: string
  win_percentage_icon?: string
  max_entries_icon?: string
}

export interface ChallengeListResponse {
  challenges: Challenge[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export interface ChallengeStats {
  total_challenges: number
  upcoming_challenges: number
  live_challenges: number
  completed_challenges: number
  draft_challenges: number
  total_participants: number
  total_prize_pool: number
}

export interface ChallengeParticipant {
  id: string
  challenge_id: string
  user_id: string
  user_email: string
  user_name: string
  entry_fee_paid: number
  joined_at: string
  current_rank?: number
  final_rank?: number
  prize_amount?: number
  is_active: boolean
}

export interface ChallengeLeaderboardResponse {
  challenge_id: string
  challenge_name: string
  participants: ChallengeParticipant[]
  total_participants: number
  last_updated: string
}

export interface ChallengeFinalizationRequest {
  challenge_id: string
  winners: Array<{
    participant_id: string
    rank: number
    prize_amount: number
  }>
  notes?: string
}

export interface ChallengeFinalizationResponse {
  message: string
  challenge_id: string
  winners_count: number
  total_prize_distributed: number
  finalization_date: string
}
export interface Holding {
  symbol: string
  quantity: number
  avg_price: number
  current_price: number
  total_spent: number
  current_value: number
  profit_loss: number
  profit_loss_percentage: number
}

export interface ChallengeParticipant {
  user_id: string
  full_name: string
  rank: number
  portfolio_value: number
  total_spent: number
  total_profit_loss: number
  profit_loss_percentage: number
  holdings: Holding[]
  last_updated: string
}

export interface ChallengeLeaderboardResponse {
  challenge_id: string
  challenge_name: string
  page: number
  limit: number
  total_participants: number
  total_pages: number
  participants: ChallengeParticipant[]
  last_updated: string
}
export type Participant = ChallengeParticipant
class ChallengeService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('adminToken')
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  async getChallenges(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    status?: string,
    type?: string,
    category?: string,
    experience?: string,
    risk?: string,
    guaranteed?: string
  ): Promise<ChallengeListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    if (status) params.append('status', status)
    if (search) params.append('search', search)
    if (type) params.append('type', type)
    if (category) params.append('category', category)
    if (experience) params.append('experience', experience)
    if (risk) params.append('risk', risk)
    if (guaranteed) params.append('guaranteed', guaranteed)

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(`/challenges?${paramsWithAppType}`, {
      method: 'GET',
    })
  }

  async getChallenge(challengeId: string): Promise<Challenge> {
    return makeApiRequest(`/challenges/${challengeId}`, {
      method: 'GET',
    })
  }

  async createChallenge(challengeData: ChallengeCreate): Promise<Challenge> {
    const bodyWithAppType = addAppTypeToBody(challengeData)
    return makeApiRequest('/challenges', {
      method: 'POST',
      body: JSON.stringify(bodyWithAppType),
    })
  }
  async getLeaderBoard(
    challengeId: string,
    page: number,
    limit: number
  ): Promise<ChallengeLeaderboardResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(
      `/leaderboard/${challengeId}?${paramsWithAppType.toString()}`,
      {
        method: 'GET',
      }
    )
  }

  async updateChallenge(
    challengeId: string,
    updateData: ChallengeUpdate
  ): Promise<Challenge> {
    const bodyWithAppType = addAppTypeToBody(updateData)
    return makeApiRequest(`/challenges/${challengeId}`, {
      method: 'PUT',
      body: JSON.stringify(bodyWithAppType),
    })
  }

  async deleteChallenge(challengeId: string): Promise<void> {
    return makeApiRequest(`/challenges/${challengeId}`, {
      method: 'DELETE',
    })
  }

  async getChallengeStats(): Promise<ChallengeStats> {
    return makeApiRequest('/challenges/stats', {
      method: 'GET',
    })
  }

  async getChallengeParticipants(
    challengeId: string,
    page: number = 1,
    perPage: number = 50
  ): Promise<ChallengeLeaderboardResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    })

    const paramsWithAppType = addAppTypeToParams(params)

    return makeApiRequest(
      `/challenges/${challengeId}/participants?${paramsWithAppType}`,
      {
        method: 'GET',
      }
    )
  }

  async publishChallenge(challengeId: string): Promise<any> {
    return makeApiRequest(`/challenges/${challengeId}/publish`, {
      method: 'POST',
    })
  }

  async cancelChallenge(challengeId: string): Promise<any> {
    return makeApiRequest(`/challenges/${challengeId}/cancel`, {
      method: 'POST',
    })
  }

  async startChallenge(challengeId: string): Promise<any> {
    return makeApiRequest(`/challenges/${challengeId}/start`, {
      method: 'POST',
    })
  }

  async completeChallenge(challengeId: string): Promise<any> {
    return makeApiRequest(`/challenges/${challengeId}/complete`, {
      method: 'POST',
    })
  }

  async finalizeChallenge(
    challengeId: string,
    finalizationData: ChallengeFinalizationRequest
  ): Promise<ChallengeFinalizationResponse> {
    const bodyWithAppType = addAppTypeToBody(finalizationData)
    return makeApiRequest(`/challenges/${challengeId}/finalize`, {
      method: 'POST',
      body: JSON.stringify(bodyWithAppType),
    })
  }

  // Helper method to format currency
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Helper method to format date
  formatDate(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Helper method to get status badge color
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'live':
        return 'bg-green-100 text-green-800'
      case 'upcoming':
        return 'bg-blue-100 text-blue-800'
      case 'completed':
        return 'bg-gray-100 text-gray-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Helper method to get type badge color
  getTypeColor(type: string): string {
    switch (type.toLowerCase()) {
      case 'guaranteed':
        return 'bg-purple-100 text-purple-800'
      case 'flexible':
        return 'bg-indigo-100 text-indigo-800'
      case 'practice':
        return 'bg-green-100 text-green-800'
      case 'weekly':
        return 'bg-blue-100 text-blue-800'
      case 'daily':
        return 'bg-orange-100 text-orange-800'
      case 'monthly':
        return 'bg-pink-100 text-pink-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }
}

export const challengeService = new ChallengeService()
